import { FC, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Autocomplete,
  Option,
} from '@beeline/design-system-react/extracted-styles';

import { Floor } from 'src/api/types';
import {
  BuildingSelect,
  BuildingSelectItem,
  toBuildingSelectItem,
} from 'src/components/BuildingSelect/BuildingSelect';
import { useFloorMap } from 'src/components/FloorMap/FloorMapProvider';
import {
  toTownSelectItem,
  TownSelect,
  TownSelectItem,
} from 'src/components/TownSelect/TownSelect';
import { getDeviceSize } from 'src/store/agent/selectors';
import { fetchFloors } from 'src/store/floors/services';
import { getFilteredRooms, getRoomsState } from 'src/store/rooms/selectors';
import { openRoom, roomsActions } from 'src/store/rooms/slice';
import { sidesheetActions } from 'src/store/sidesheet/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { DeviceSize, PlaceType } from 'src/types';

import { RoomBookingFilterButton } from '../../RoomBookingFilters/RoomBookingFilterButton';
import { RoomBookingDateField } from '../../RoomBookingSettings/RoomBookingDateField';
import { RoomBookingTimeField } from '../../RoomBookingSettings/RoomBookingTimeField';
import { RoomOfficeSidesheetButton } from '../../RoomOffice/RoomOfficeSidesheetButton';

import classes from './DesktopPageActions.module.scss';

const renderValue = (option: Option<string>): string => option.value ?? '';

export const DesktopPageActions: FC = () => {
  const deviceSize = useSelector(getDeviceSize);
  const dispatch = useAppDispatch();
  const { town, building } = useSelector(getRoomsState);

  const handleTownChange = useCallback(
    (value: TownSelectItem | undefined) => {
      dispatch(sidesheetActions.close());
      dispatch(roomsActions.setTown(value?.value));
      dispatch(roomsActions.setBuilding(undefined));
      dispatch(roomsActions.setFloor(undefined));
    },
    [dispatch],
  );

  const handleBuildingChange = useCallback(
    async (value: BuildingSelectItem | undefined) => {
      let floor: Floor | undefined;
      dispatch(sidesheetActions.close());
      dispatch(roomsActions.setBuilding(value?.value));

      if (value?.value) {
        const floors = await dispatch(
          fetchFloors({
            cached: true,
            buildingId: value.value.buildingId,
          }),
        ).unwrap();
        floor = floors.find(f => !f.isParking);
      }

      dispatch(roomsActions.setFloor(floor));
    },
    [dispatch],
  );

  const [roomSearchValue, setRoomSearchValue] = useState<Option<string> | null>(
    null,
  );
  const [roomSearchInput, setRoomSearchInput] = useState('');
  const filteredBookingObjects = useSelector(getFilteredRooms);
  const { scrollTo } = useFloorMap();

  const roomSearchOptions = useMemo((): Option<string>[] => {
    const result: Option<string>[] = [];

    for (const [key, value] of filteredBookingObjects) {
      if (!key || !value.title) continue;

      result.push({ id: key, value: value.title });
    }

    return result.sort((a, b) => a.value.localeCompare(b.value));
  }, [filteredBookingObjects]);

  const filteredRoomSearchOptions = useMemo((): Option<string>[] => {
    const search = roomSearchInput.toLowerCase();

    return roomSearchOptions.filter(option =>
      option.value.toLowerCase().includes(search),
    );
  }, [roomSearchInput, roomSearchOptions]);

  const handleRoomSearchChange = useCallback(
    async (option: Option<string>) => {
      setRoomSearchValue(option);

      const found = filteredBookingObjects.get(option.id as string);
      if (found && found.type === PlaceType.Room) {
        dispatch(openRoom(found.mapPlaceId));

        scrollTo(found);
      }
    },
    [dispatch, filteredBookingObjects, scrollTo],
  );

  const handleRoomSearchClear = useCallback(() => {
    setRoomSearchValue(null);
  }, []);

  const townSelectItem = useMemo(() => toTownSelectItem(town), [town]);
  const buildingSelectItem = useMemo(
    () => toBuildingSelectItem(building),
    [building],
  );

  return (
    <div className={classes.actions}>
      {deviceSize !== DeviceSize.S && (
        <div className={classes.main}>
          <div className={classes.town}>
            <TownSelect town={townSelectItem} onChange={handleTownChange} />
          </div>
          <div className={classes.building}>
            <BuildingSelect
              building={buildingSelectItem}
              onChange={handleBuildingChange}
              townId={town?.townId}
              disabled={!town}
              key={town?.townId}
            />
          </div>
        </div>
      )}
      <div className={classes.additional}>
        {deviceSize === DeviceSize.S && <RoomOfficeSidesheetButton small />}
        <div className={classes.date}>
          <RoomBookingDateField />
        </div>
        <div className={classes.time}>
          <RoomBookingTimeField />
        </div>
        <div className={classes.controls}>
          <div className={classes.roomSearch}>
            <Autocomplete
              placeholder='Номер'
              type='search'
              size='medium'
              disabled={!roomSearchOptions.length}
              options={filteredRoomSearchOptions}
              value={roomSearchValue}
              noOptionsText='не найдено'
              onChange={handleRoomSearchChange}
              renderValue={renderValue}
              onInputChange={setRoomSearchInput}
              onInputClear={handleRoomSearchClear}
              loading={!roomSearchOptions.length}
              fullWidth
            />
          </div>
          <RoomBookingFilterButton />
        </div>
      </div>
    </div>
  );
};
